import React, { useState, useEffect, useCallback } from 'react';
import { db, auth, appId } from '../../firebase-config';
import { collection, addDoc, onSnapshot, query, orderBy, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { PRAZO_CONFIG, calcularFeriadosMoveis, VALIDACOES } from './PrazoConfig';
import FeriadosManager from './FeriadosManager';
import SuspensoesPrazo from './SuspensoesPrazo';
import { CalendarIcon, ClockIcon, ExclamationTriangleIcon, ScaleIcon, XMarkIcon } from '../Icons';

const PrazoCalculator = ({ isOpen, onClose, processoId, onSave, prazoParaEditar, feriados, suspensoes }) => {
  const userId = auth.currentUser?.uid;

  // Estados do formulário
  const [dataInicio, setDataInicio] = useState('');
  const [quantidadeDias, setQuantidadeDias] = useState('');
  const [tipoContagem, setTipoContagem] = useState(PRAZO_CONFIG.TIPOS_CONTAGEM.DIAS_UTEIS_JURIDICOS);
  const [modalidade, setModalidade] = useState(PRAZO_CONFIG.MODALIDADES.SIMPLES);
  const [descricao, setDescricao] = useState('');
  const [urgente, setUrgente] = useState(false);

  // Estados de resultado
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [historico, setHistorico] = useState([]);
  
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (prazoParaEditar && isOpen) {
        setIsEditMode(true);
        const dataInicioDate = prazoParaEditar.dataInicio?.toDate ? prazoParaEditar.dataInicio.toDate() : new Date(prazoParaEditar.dataInicio);
        setDataInicio(dataInicioDate.toISOString().split('T')[0]);
        setQuantidadeDias(prazoParaEditar.diasOriginais || '');
        setTipoContagem(prazoParaEditar.tipoContagem || PRAZO_CONFIG.TIPOS_CONTAGEM.DIAS_UTEIS_JURIDICOS);
        setModalidade(prazoParaEditar.modalidade || PRAZO_CONFIG.MODALIDADES.SIMPLES);
        setDescricao(prazoParaEditar.descricao || '');
        setUrgente(prazoParaEditar.urgente || false);
        setResultado(prazoParaEditar);
    } else {
        setIsEditMode(false);
        resetForm();
    }
  }, [prazoParaEditar, isOpen]);

  // Carregar histórico de cálculos
  useEffect(() => {
    if (!userId || !isOpen) return;

    const historicoRef = collection(db, `artifacts/${appId}/users/${userId}/prazos_historico`);
    const historicoQuery = query(historicoRef, orderBy('dataCalculo', 'desc'));

    const unsubscribe = onSnapshot(historicoQuery, (snapshot) => {
      const historicoData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          dataInicio: data.dataInicio?.toDate ? data.dataInicio.toDate() : null,
          dataFim: data.dataFim?.toDate ? data.dataFim.toDate() : null,
          dataCalculo: data.dataCalculo?.toDate ? data.dataCalculo.toDate() : null,
          diasDetalhados: Array.isArray(data.diasDetalhados) ? data.diasDetalhados.map(dia => ({
            ...dia,
            data: dia.data?.toDate ? dia.data.toDate() : null
          })) : [],
        };
      });
      setHistorico(historicoData);
    });

    return () => unsubscribe();
  }, [userId, appId, isOpen]);

  const handleSelectFromHistory = (item) => {
    const dataInicioDate = item.dataInicio?.toDate ? item.dataInicio.toDate() : new Date(item.dataInicio);
    setDataInicio(dataInicioDate.toISOString().split('T')[0]);
    setQuantidadeDias(item.diasOriginais || '');
    setTipoContagem(item.tipoContagem || PRAZO_CONFIG.TIPOS_CONTAGEM.DIAS_UTEIS_JURIDICOS);
    setModalidade(item.modalidade || PRAZO_CONFIG.MODALIDADES.SIMPLES);
    setDescricao(item.descricao || '');
    setUrgente(item.urgente || false);
    setResultado(item);
  }

  // Função para verificar se é feriado
  const isFeriado = useCallback((data) => {
    if (!data) return false;
    const dataChecar = new Date(data.getFullYear(), data.getMonth(), data.getDate());

    const feriadoFixo = PRAZO_CONFIG.FERIADOS_NACIONAIS_FIXOS.find(
      f => f.dia === dataChecar.getDate() && (f.mes - 1) === dataChecar.getMonth()
    );
    if (feriadoFixo) return true;

    const feriadosMoveis = calcularFeriadosMoveis(dataChecar.getFullYear());
    if (feriadosMoveis.some(f => f.data.getTime() === dataChecar.getTime())) return true;

    return feriados.some(f => {
      if (!f.data || !f.ativo) return false;
      const dataFeriadoFirestore = f.data.toDate ? f.data.toDate() : new Date(f.data);
      const dataFeriado = new Date(dataFeriadoFirestore.getFullYear(), dataFeriadoFirestore.getMonth(), dataFeriadoFirestore.getDate());
      return dataFeriado.getTime() === dataChecar.getTime();
    });
  }, [feriados]);

  // Função para verificar se está em suspensão
  const isSuspensao = useCallback((data) => {
     if (!data) return false;
     const dataChecar = new Date(data.getFullYear(), data.getMonth(), data.getDate());

    return suspensoes.some(s => {
      if (!s.dataInicio || !s.dataFim || !s.ativo) return false;
      const inicioFirestore = s.dataInicio.toDate ? s.dataInicio.toDate() : new Date(s.dataInicio);
      const fimFirestore = s.dataFim.toDate ? s.dataFim.toDate() : new Date(s.dataFim);
      
      const inicio = new Date(inicioFirestore.getFullYear(), inicioFirestore.getMonth(), inicioFirestore.getDate());
      const fim = new Date(fimFirestore.getFullYear(), fimFirestore.getMonth(), fimFirestore.getDate());
      return dataChecar >= inicio && dataChecar <= fim;
    });
  }, [suspensoes]);

  // Função para verificar se é dia útil
  const isDiaUtil = useCallback((data, tipo) => {
    const diaSemana = data.getDay();

    if (tipo === PRAZO_CONFIG.TIPOS_CONTAGEM.CORRIDO) return true;
    if (diaSemana === 0 || diaSemana === 6) return false;
    if (tipo === PRAZO_CONFIG.TIPOS_CONTAGEM.DIAS_UTEIS_JURIDICOS) {
      if (isFeriado(data)) return false;
      if (isSuspensao(data)) return false;
    }
    return true;
  }, [isFeriado, isSuspensao]);
    
  // Função principal de cálculo
  const calcularPrazo = useCallback(() => {
    if (!VALIDACOES.isDataValida(new Date(dataInicio))) {
      toast.error('Data de início inválida');
      return;
    }
    const diasPrazo = parseInt(quantidadeDias, 10);
    if (!VALIDACOES.isPrazoPositivo(diasPrazo)) {
      toast.error('Quantidade de dias deve ser um número positivo');
      return;
    }

    setLoading(true);
    try {
      const inicio = new Date(dataInicio + 'T12:00:00Z');
      let diasCalculados = modalidade === PRAZO_CONFIG.MODALIDADES.DOBRO ? diasPrazo * 2 : diasPrazo;
      let dataAtual = new Date(inicio);

      if (PRAZO_CONFIG.REGRAS.INICIO_PRAZO_DIA_SEGUINTE) {
        dataAtual.setDate(dataAtual.getDate() + 1);
      }
      
      while (!isDiaUtil(dataAtual, tipoContagem)) {
        dataAtual.setDate(dataAtual.getDate() + 1);
      }

      let diasContados = 0;
      let dataFinal = new Date(dataAtual);
      
      while (diasContados < diasCalculados) {
        if(diasContados > 0) dataFinal.setDate(dataFinal.getDate() + 1);
        if (isDiaUtil(dataFinal, tipoContagem)) {
          diasContados++;
        }
      }

      while (!isDiaUtil(dataFinal, tipoContagem)) {
          dataFinal.setDate(dataFinal.getDate() + 1);
      }
      
      let diasDetalhados = [];
      let diasNaoUteis = 0;
      let diaIteracao = new Date(dataAtual);
      while(diaIteracao <= dataFinal) {
        const diaUtil = isDiaUtil(diaIteracao, tipoContagem);
        if(!diaUtil) diasNaoUteis++;
        diasDetalhados.push({
            data: new Date(diaIteracao),
            diaUtil,
            diaSemana: diaIteracao.toLocaleDateString('pt-BR', { weekday: 'long' }),
            feriado: isFeriado(diaIteracao),
            suspensao: isSuspensao(diaIteracao),
            prorrogacao: false
        });
        diaIteracao.setDate(diaIteracao.getDate() + 1);
      }
      
      const resultadoCalculo = {
        dataInicio: new Date(inicio), dataFim: new Date(dataFinal),
        diasOriginais: diasPrazo, diasCalculados, diasNaoUteis,
        diasCorridos: diasDetalhados.length, tipoContagem, modalidade, descricao, urgente, diasDetalhados,
      };

      setResultado(resultadoCalculo);
      
      if(isEditMode) {
          handleUpdatePrazo(resultadoCalculo);
      } else {
          salvarNoHistorico(resultadoCalculo);
      }

    } catch (error) {
      console.error('Erro ao calcular prazo:', error);
      toast.error('Ocorreu um erro inesperado ao calcular o prazo.');
    } finally {
      setLoading(false);
    }
  }, [dataInicio, quantidadeDias, tipoContagem, modalidade, descricao, urgente, isDiaUtil, isEditMode, prazoParaEditar]);
  
  const handleUpdatePrazo = async (resultado) => {
    if (!userId || !prazoParaEditar?.id) return;
    try {
        const prazoRef = doc(db, `artifacts/${appId}/users/${userId}/prazos_historico`, prazoParaEditar.id);
        await updateDoc(prazoRef, {
            ...resultado,
            dataCalculo: serverTimestamp(),
        });
        toast.success('Prazo atualizado com sucesso!');
        onClose();
    } catch (error) {
        console.error('Erro ao atualizar prazo:', error);
        toast.error('Erro ao atualizar o prazo.');
    }
  };

  // Salvar no histórico usando serverTimestamp
  const salvarNoHistorico = async (resultado) => {
    if (!userId) return;
    try {
      await addDoc(collection(db, `artifacts/${appId}/users/${userId}/prazos_historico`), {
        ...resultado,
        processoId: processoId || null,
        dataCalculo: serverTimestamp(),
        createdAt: serverTimestamp()
      });
      toast.success('Cálculo salvo no histórico!');
    } catch (error) {
      console.error('Erro ao salvar no histórico:', error);
    }
  };

  const resetForm = () => {
    setDataInicio(''); setQuantidadeDias('');
    setTipoContagem(PRAZO_CONFIG.TIPOS_CONTAGEM.DIAS_UTEIS_JURIDICOS);
    setModalidade(PRAZO_CONFIG.MODALIDADES.SIMPLES);
    setDescricao(''); setUrgente(false); setResultado(null);
    setIsEditMode(false);
  };

  if (!isOpen) return null;

  const renderResultado = (res) => {
    if (!res || !res.dataFim) return null;
    const dataFimDate = res.dataFim instanceof Date ? res.dataFim : new Date(res.dataFim);
    const dataInicioDate = res.dataInicio instanceof Date ? res.dataInicio : new Date(res.dataInicio);

    return (
        <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                {res.modalidade === 'dobro' && <div className="text-sm text-red-600 mb-1">🚨 PRAZO EM DOBRO</div>}
                <div className="text-2xl font-bold text-blue-800">{dataFimDate.toLocaleDateString('pt-BR')}</div>
                <div className="text-sm text-blue-600">{dataFimDate.toLocaleDateString('pt-BR', { weekday: 'long' })}</div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Início:</span><br /><span className="font-medium">{dataInicioDate.toLocaleDateString('pt-BR')}</span></div>
                <div><span className="text-gray-500">Fim:</span><br /><span className="font-medium">{dataFimDate.toLocaleDateString('pt-BR')}</span></div>
                <div><span className="text-gray-500">Dias do prazo:</span><br /><span className="font-medium">{res.diasOriginais}</span></div>
                <div><span className="text-gray-500">Dias contados:</span><br /><span className="font-medium">{res.diasCalculados}</span></div>
                <div><span className="text-gray-500">Dias corridos:</span><br /><span className="font-medium">{res.diasCorridos}</span></div>
                <div><span className="text-gray-500">Dias não úteis:</span><br /><span className="font-medium">{res.diasNaoUteis}</span></div>
            </div>
            <div className="max-h-40 overflow-y-auto border rounded-lg p-2 bg-gray-50">
                <div className="text-xs text-gray-500 mb-2 font-semibold">Linha do Tempo do Prazo:</div>
                <div className="space-y-1">
                    {res.diasDetalhados.map((dia, index) => {
                      const diaDate = dia.data instanceof Date ? dia.data : new Date(dia.data);
                      return (
                        <div key={index} className={`flex justify-between items-center px-2 py-1 rounded text-xs ${ dia.diaUtil ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800' }`}>
                            <span>{diaDate.toLocaleDateString('pt-BR', {day: '2-digit', month:'2-digit'})}</span>
                            <span>{dia.diaSemana.slice(0, 3)}.</span>
                            <span className='font-bold'>{dia.diaUtil ? 'ÚTIL' : 'NÃO ÚTIL'}</span>
                            <span className="text-right">
                                {dia.feriado && '🎉 Feriado'}
                                {dia.suspensao && '⚖️ Suspensão'}
                            </span>
                        </div>
                    )})}
                </div>
            </div>
        </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl h-5/6 overflow-hidden flex">
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <ScaleIcon className="h-8 w-8 text-blue-600" />
              {isEditMode ? 'Editar Prazo' : 'Calculadora de Prazos Jurídicos'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2 rounded-lg"><XMarkIcon className="h-6 w-6" /></button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Dados do Prazo</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data de Início (Ciência)</label>
                <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"/>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantidade de Dias</label>
                <input type="number" min="1" value={quantidadeDias} onChange={(e) => setQuantidadeDias(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Ex: 15"/>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Contagem</label>
                <select value={tipoContagem} onChange={(e) => setTipoContagem(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value={PRAZO_CONFIG.TIPOS_CONTAGEM.DIAS_UTEIS_JURIDICOS}>Dias Úteis Jurídicos (Padrão CPC)</option>
                  <option value={PRAZO_CONFIG.TIPOS_CONTAGEM.DIAS_UTEIS}>Dias Úteis (seg-sex)</option>
                  <option value={PRAZO_CONFIG.TIPOS_CONTAGEM.CORRIDO}>Dias Corridos</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Modalidade</label>
                <select value={modalidade} onChange={(e) => setModalidade(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value={PRAZO_CONFIG.MODALIDADES.SIMPLES}>Prazo Simples</option>
                  <option value={PRAZO_CONFIG.MODALIDADES.DOBRO}>Prazo em Dobro</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <input type="text" value={descricao} onChange={(e) => setDescricao(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Ex: Contestação, Recurso, etc."/>
              </div>
              <div className="flex items-center"><input type="checkbox" id="urgente" checked={urgente} onChange={(e) => setUrgente(e.target.checked)} className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"/><label htmlFor="urgente" className="ml-2 text-sm text-gray-700 flex items-center gap-1"><ExclamationTriangleIcon className="h-4 w-4 text-red-500" />Prazo Urgente</label></div>
              <div className="flex gap-2 pt-4">
                <button onClick={calcularPrazo} disabled={loading || !dataInicio || !quantidadeDias} className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2">
                  {loading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <ClockIcon className="h-4 w-4" />}
                  {loading ? 'Calculando...' : 'Calcular e Salvar'}
                </button>
                <button onClick={resetForm} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Limpar</button>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center border-b pb-2">
                <h3 className="text-lg font-semibold text-gray-700">Resultado</h3>
                {resultado && onSave && !isEditMode && (<button onClick={onClose} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm">Salvar como Tarefa</button>)}
              </div>
              {resultado ? renderResultado(resultado) : (<div className="text-center text-gray-500 py-8"><CalendarIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" /><p>Preencha os dados e clique em "Calcular Prazo"</p></div>)}
            </div>
          </div>
        </div>
        <div className="w-80 bg-gray-50 border-l p-4 overflow-y-auto">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Histórico de Cálculos</h3>
          <div className="space-y-3">
            {historico.slice(0, 15).map((item) => (
              <div key={item.id} className="bg-white rounded-lg p-3 shadow-sm border cursor-pointer hover:bg-gray-50" onClick={() => handleSelectFromHistory(item)}>
                <div className="text-sm font-medium text-gray-800 truncate">{item.descricao || 'Prazo jurídico'}</div>
                <div className="text-xs text-gray-500 mt-1">{item.diasOriginais} dias • {item.tipoContagem.replace(/_/g, ' ')}</div>
                <div className="text-xs text-blue-600 mt-1">Vence: {item.dataFim ? item.dataFim.toLocaleDateString('pt-BR') : 'N/A'}</div>
                <div className="text-xs text-gray-400 mt-1">Calc.: {item.dataCalculo ? item.dataCalculo.toLocaleString('pt-BR') : 'N/A'}</div>
              </div>
            ))}
            {historico.length === 0 && (<div className="text-center text-gray-500 py-8"><ClockIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" /><p className="text-sm">Nenhum cálculo ainda</p></div>)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrazoCalculator;
